import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FilterGroup from "@/components/feature/FilterGroup";
import NewsListItem from "@/components/feature/NewsListItem";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { countNewsByCategory, filterNews, newsList, type DateRange } from "@/lib/content";
import { newsCategories, type NewsCategory } from "@/mocks/news";

const PAGE_SIZE = 5;
type CategoryFilter = NewsCategory | "all";

export default function NewsCategoryPage() {
  useDocumentMeta(
    "影视资讯 - 小电影影视动态快报",
    "即时更新影视热点、业界新闻与活动报道，从短片节展到行业趋势，一站读完当天值得关注的影视动态。",
    "影视资讯,影视新闻,行业新闻,影评,活动报道,小电影",
  );

  const [category, setCategory] = useState<CategoryFilter>("all");
  const [range, setRange] = useState<DateRange>("all");
  const [page, setPage] = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);

  const items = useMemo(() => filterNews(category, range), [category, range]);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [category, range]);

  const goToPage = (next: number) => {
    setPage(Math.min(Math.max(next, 1), totalPages));
    window.scrollTo({ top: 260, behavior: "smooth" });
  };

  const resetFilters = () => {
    setCategory("all");
    setRange("all");
    setPage(1);
    setSheetOpen(false);
  };

  const filterPanel = (
    <>
      <FilterGroup
        label="类别"
        value={category}
        onChange={(value) => setCategory(value as CategoryFilter)}
        options={[
          { value: "all", label: "全部", count: newsList.length },
          ...newsCategories.map((item) => ({
            value: item,
            label: item,
            count: countNewsByCategory(item),
          })),
        ]}
      />
      <div className="h-px bg-background-200" />
      <FilterGroup
        label="日期范围"
        value={range}
        onChange={(value) => setRange(value as DateRange)}
        options={[
          { value: "all", label: "全部时间" },
          { value: "1m", label: "最近一个月" },
          { value: "3m", label: "最近三个月" },
        ]}
      />
    </>
  );

  const activeFilterCount = (category !== "all" ? 1 : 0) + (range !== "all" ? 1 : 0);

  return (
    <PageShell>
      <section className="bg-background-100/70 border-b border-background-200">
        <div className="container-page py-6 md:py-8">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />
          <div className="mt-3 flex flex-col md:flex-row md:items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground-950">
                影视资讯
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                最新影视动态、幕后故事与业界资讯，每天整理，按发布时间倒序排列。
              </p>
            </div>
            <p className="text-xs text-foreground-500">
              共 <span className="font-label font-bold text-primary-600">{items.length}</span> 条资讯
            </p>
          </div>
        </div>
      </section>

      <div className="container-page py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <aside className="hidden lg:block w-[268px] shrink-0">
            <div className="sticky top-[126px] rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between pb-4">
                <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                  <i className="ri-filter-3-line text-primary-600" />
                  筛选资讯
                </h2>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-[12px] text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  重置
                </button>
              </div>
              {filterPanel}

              <div className="mt-5 pt-4 border-t border-background-200">
                <h3 className="text-[13px] font-semibold text-foreground-800 mb-2">热门短片</h3>
                <ul className="space-y-2">
                  {["归途", "逆光飞行", "今天不加班"].map((title, titleIndex) => (
                    <li key={title} className="text-[12px] text-foreground-600">
                      <span className="font-label text-accent-600 font-bold mr-1.5">
                        0{titleIndex + 1}
                      </span>
                      {title}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/category/shortfilms"
                  className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  浏览短片分类
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSheetOpen(true)}
                  className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-300 bg-background-50 text-[13px] text-foreground-800 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-filter-3-line" />
                  筛选
                  {activeFilterCount > 0 && (
                    <span className="ml-0.5 w-5 h-5 rounded-full bg-primary-500 text-background-50 text-[11px] flex items-center justify-center font-label">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <div className="hidden sm:flex items-center gap-1 p-1 rounded-full bg-background-100">
                  {[{ value: "all", label: "全部" }, ...newsCategories.map((item) => ({ value: item, label: item }))].map(
                    (option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setCategory(option.value as CategoryFilter)}
                        className={`h-7 px-3 rounded-full text-[12px] transition-colors cursor-pointer whitespace-nowrap ${
                          category === option.value
                            ? "bg-background-50 text-primary-600 font-medium"
                            : "text-foreground-600 hover:text-foreground-900"
                        }`}
                      >
                        {option.label}
                      </button>
                    ),
                  )}
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-[12px] text-foreground-500">
                <i className="ri-sort-desc" />
                时间倒序
              </span>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                  {pageItems.map((item) => (
                    <NewsListItem key={item.id} item={item} />
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="上一页"
                    className="w-9 h-9 rounded-md border border-background-300 flex items-center justify-center text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }, (_, pageIndex) => pageIndex + 1).map((number) => (
                    <button
                      key={number}
                      type="button"
                      onClick={() => goToPage(number)}
                      aria-current={number === currentPage ? "page" : undefined}
                      className={`w-9 h-9 rounded-md text-[13px] font-label transition-colors cursor-pointer ${
                        number === currentPage
                          ? "bg-primary-500 text-background-50 font-bold"
                          : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                      }`}
                    >
                      {number}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="下一页"
                    className="w-9 h-9 rounded-md border border-background-300 flex items-center justify-center text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
                <p className="mt-3 text-center text-[11px] text-foreground-400">
                  第 {currentPage} / {totalPages} 页 · 每页 {PAGE_SIZE} 条
                </p>
              </>
            ) : (
              <EmptyState
                title="这段时间还没有相关资讯"
                description="换个日期范围或类别看看，也可以先去短片分类逛逛。"
                actionLabel="重置筛选"
                onAction={resetFilters}
                icon="ri-newspaper-line"
              />
            )}

            {items.length === 0 && (
              <p className="mt-4 text-center text-[12px] text-foreground-500">
                想看点别的？
                <Link to="/category/shortfilms" className="ml-1 text-primary-600 hover:underline cursor-pointer">
                  去短片分类
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>

      {sheetOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex items-end bg-foreground-950/60 animate-fade-in"
          role="dialog"
          aria-modal="true"
          aria-label="筛选面板"
          onClick={() => setSheetOpen(false)}
        >
          <div
            className="w-full bg-background-50 rounded-t-lg p-5 animate-slide-up"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-4 border-b border-background-200">
              <h2 className="text-base font-bold text-foreground-950">筛选资讯</h2>
              <button
                type="button"
                aria-label="关闭筛选面板"
                onClick={() => setSheetOpen(false)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="py-2">{filterPanel}</div>
            <div className="flex gap-3 pt-4 border-t border-background-200">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 h-10 rounded-md border border-background-300 text-sm text-foreground-800 cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex-1 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                查看 {items.length} 条结果
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}